import React, { useEffect, useState } from 'react';
import { CreateSelectionRequest, SelectionSummary, Sport } from '../Model';
import { restClient } from '../RestClient';

interface AddSelectionModalProps {
  onClose: () => void;
  onSuccess: (newSelection: SelectionSummary) => void;
}

export const AddSelectionModal: React.FC<AddSelectionModalProps> = ({
  onClose,
  onSuccess,
}) => {
  const [name, setName] = useState('');
  const [sport, setSport] = useState<Sport>('Football');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    const request: CreateSelectionRequest = {
      name: name.trim(),
      sport,
    };

    const result = await restClient.createSelection(request);

    if (result.IsSuccess) {
      onSuccess(result.Value);
      onClose();
    } else {
      setError(result.Error.description);
    }

    setIsSubmitting(false);
  };

  const isFormValid = name.trim() !== '';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-selection-modal-title"
        className="modal modal-sm"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="modal-header">
          <h2 id="add-selection-modal-title" className="modal-title">Add Team Selection</h2>
          <button onClick={onClose} className="btn-close btn-close-lg" aria-label="Close">
            ×
          </button>
        </div>

        {/* Modal Body */}
        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            {/* Error display */}
            {error && (
              <div className="alert alert-error mb-4">
                {error}
              </div>
            )}

            {/* Sport selection */}
            <div className="form-group">
              <label htmlFor="selection-sport" className="form-label form-label-required">
                Sport
              </label>
              <select
                id="selection-sport"
                value={sport}
                onChange={(e) => setSport(e.target.value as Sport)}
                className="form-select"
                disabled={isSubmitting}
              >
                <option value="Football">Football</option>
                <option value="Rugby">Rugby</option>
              </select>
            </div>

            {/* Name input */}
            <div className="form-group">
              <label htmlFor="selection-name" className="form-label form-label-required">
                Selection Name
              </label>
              <input
                id="selection-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="form-control"
                placeholder="Enter a name for your team selection"
                required
                disabled={isSubmitting}
                autoFocus
              />
            </div>
          </form>
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary" disabled={isSubmitting}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isFormValid || isSubmitting}
            className="btn btn-primary"
          >
            {isSubmitting ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};
